import { bunja } from 'bunja';
import { Atom, atom, type Getter } from 'jotai';
import { withAtomEffect } from 'jotai-effect';

import { atomWithRafThrottle } from '@flipster/react/misc/state/atom-with-raf-throttle';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { symbolBunja } from './base';
import { OrderState, convert, formBunja, getKindValue } from './form';
import { resolveOrderPrice } from './logic/derivation/order-price';

export const realtimePriceBunja = bunja(() => {
  const { askPriceAtom: pAskPriceAtom, bidPriceAtom: pBidPriceAtom } = bunja.use(symbolBunja);
  const { referencePriceAtom, triggerPriceAtom, limitPriceAtom, priceTypeAtom, isBboAtom, triggerExecutionTypeAtom } =
    bunja.use(formBunja);
  /**
   * Trigger Limit / Limit 주문은 limit price(BBO 시 ticker bid/ask) 기반, Trigger Market 등은 trigger price 기반.
   * trigger price ≠ order price.
   */
  const resolveParams = (get: Getter, fallbackPrice: string) => ({
    priceType: get(priceTypeAtom),
    triggerExecutionType: get(triggerExecutionTypeAtom),
    isBbo: get(isBboAtom),
    limitPrice: get(limitPriceAtom),
    triggerPrice: get(triggerPriceAtom),
    fallbackPrice,
  });
  const askPriceAtom = atom<string>((get) => resolveOrderPrice(resolveParams(get, get(pAskPriceAtom))));
  const bidPriceAtom = atom<string>((get) => resolveOrderPrice(resolveParams(get, get(pBidPriceAtom))));
  const priceAtom = atom((get) => resolveOrderPrice(resolveParams(get, get(referencePriceAtom))));

  return { askPriceAtom, bidPriceAtom, priceAtom };
});
realtimePriceBunja.debugLabel = 'realtimePriceBunja';

export const throttledPriceBunja = bunja(() => {
  const { askPriceAtom: pAskPriceAtom, bidPriceAtom: pBidPriceAtom } = bunja.use(symbolBunja);
  const { referencePriceAtom, triggerPriceAtom, limitPriceAtom, priceTypeAtom, isBboAtom, triggerExecutionTypeAtom } =
    bunja.use(formBunja);

  /** validation을 위해서 1초 throttle된 reference price 사용 */
  const throttledReferencePriceAtom = atom(
    (get) => get(throttledReferencePriceBaseAtom)?.value ?? get(referencePriceAtom),
  );
  const throttledReferencePriceBaseAtom = withAtomEffect(
    atom<{ value: string; timestamp: number } | undefined>(undefined),
    (get, set) => {
      get(referencePriceAtom); // does not depend on this if omitted;
      const prev = get(throttledReferencePriceBaseAtom);
      if (!prev || Date.now() - prev.timestamp >= 3000) update();
      const id = setTimeout(update, 1000);
      return () => clearTimeout(id);
      function update() {
        if (prev && prev.value === get.peek(referencePriceAtom)) return;
        set.recurse(throttledReferencePriceBaseAtom, { value: get(referencePriceAtom), timestamp: Date.now() });
      }
    },
  );

  const resolveParams = (get: Getter, fallbackPrice: string) => ({
    priceType: get(priceTypeAtom),
    triggerExecutionType: get(triggerExecutionTypeAtom),
    isBbo: get(isBboAtom),
    limitPrice: get(limitPriceAtom),
    triggerPrice: get(triggerPriceAtom),
    fallbackPrice,
  });
  const askPriceAtom = atom<string>((get) => resolveOrderPrice(resolveParams(get, get(pAskPriceAtom))));
  const bidPriceAtom = atom<string>((get) => resolveOrderPrice(resolveParams(get, get(pBidPriceAtom))));
  const priceAtom = atom((get) => resolveOrderPrice(resolveParams(get, get(throttledReferencePriceAtom))));
  return { throttledReferencePriceAtom, priceAtom, askPriceAtom, bidPriceAtom };
});
throttledPriceBunja.debugLabel = 'throttledPriceBunja';

export const realtimeStateBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbolInfoAtom } = bunja.use(symbolBunja);
  const { orderStateAtom } = bunja.use(formBunja);
  const { priceAtom, askPriceAtom, bidPriceAtom } = bunja.use(realtimePriceBunja);
  return createStateAtoms({
    orderStateAtom,
    priceAtom,
    askPriceAtom,
    bidPriceAtom,
    tradeQtyPrecision: store.get(symbolInfoAtom).tradeQtyPrecision,
  });
});
realtimeStateBunja.debugLabel = 'realtimeStateBunja';

export const throttledStateBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbolInfoAtom } = bunja.use(symbolBunja);
  const { orderStateAtom } = bunja.use(formBunja);
  const { priceAtom, askPriceAtom, bidPriceAtom } = bunja.use(throttledPriceBunja);
  return createStateAtoms({
    orderStateAtom,
    priceAtom,
    askPriceAtom,
    bidPriceAtom,
    tradeQtyPrecision: store.get(symbolInfoAtom).tradeQtyPrecision,
  });
});
throttledStateBunja.debugLabel = 'throttledStateBunja';

/**
 * 표시 전용 가격 트랙 — realtimePriceBunja의 ask/bid/price를 프레임당 1회(rAF)로 병합한다.
 * 소켓 tick마다가 아니라 프레임 레이트로 리렌더를 캡핑하는 용도.
 * 계산·검증·제출 경로에는 절대 쓰지 말 것(그쪽은 realtime* / source atom 사용).
 * 반환 atom은 최대 1프레임 지연을 노출하기 위해 throttled* prefix를 붙인다.
 */
export const displayPriceBunja = bunja(() => {
  const { askPriceAtom, bidPriceAtom, priceAtom } = bunja.use(realtimePriceBunja);
  const throttledAskPriceAtom = atomWithRafThrottle(askPriceAtom);
  const throttledBidPriceAtom = atomWithRafThrottle(bidPriceAtom);
  const throttledPriceAtom = atomWithRafThrottle(priceAtom);
  return { throttledAskPriceAtom, throttledBidPriceAtom, throttledPriceAtom };
});
displayPriceBunja.debugLabel = 'displayPriceBunja';

/**
 * 표시 전용 파생 상태 트랙 — displayPriceBunja(프레임 스로틀) 기반으로 realtimeStateBunja와
 * 동일한 파생값(amount·quantity·margin·askX·bidX)을 만든다. orderState(입력)는 직접 dep이라
 * keystroke에는 즉시 반영되고, 가격 tick만 프레임당 1회로 병합된다. 표시 컴포넌트 전용.
 */
export const displayStateBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbolInfoAtom } = bunja.use(symbolBunja);
  const { orderStateAtom } = bunja.use(formBunja);
  const { throttledPriceAtom, throttledAskPriceAtom, throttledBidPriceAtom } = bunja.use(displayPriceBunja);
  const state = createStateAtoms({
    orderStateAtom,
    priceAtom: throttledPriceAtom,
    askPriceAtom: throttledAskPriceAtom,
    bidPriceAtom: throttledBidPriceAtom,
    tradeQtyPrecision: store.get(symbolInfoAtom).tradeQtyPrecision,
  });
  // 소비처(UI)에서 ≤1프레임 지연된 표시값임이 드러나도록 throttled* prefix로 노출한다.
  return {
    throttledMarginAtom: state.marginAtom,
    throttledQuantityAtom: state.quantityAtom,
    throttledAmountAtom: state.amountAtom,
    throttledAskMarginAtom: state.askMarginAtom,
    throttledBidMarginAtom: state.bidMarginAtom,
    throttledAskQuantityAtom: state.askQuantityAtom,
    throttledBidQuantityAtom: state.bidQuantityAtom,
    throttledAskAmountAtom: state.askAmountAtom,
    throttledBidAmountAtom: state.bidAmountAtom,
  };
});
displayStateBunja.debugLabel = 'displayStateBunja';

interface StateSources {
  orderStateAtom: Atom<OrderState>;
  priceAtom: Atom<string>;
  askPriceAtom: Atom<string>;
  bidPriceAtom: Atom<string>;
  tradeQtyPrecision: number | undefined;
}
function createStateAtoms({ orderStateAtom, priceAtom, askPriceAtom, bidPriceAtom, tradeQtyPrecision }: StateSources) {
  // quantity
  const quantityAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    const price = get(priceAtom);
    const quantityPrecision = tradeQtyPrecision;
    const stateTo = convert(state.leverage, price, { quantityPrecision })[state.kind](getKindValue(state));
    return stateTo.quantity.toString();
  });
  const askQuantityAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'margin') {
      const askPrice = get(askPriceAtom);
      const quantityPrecision = tradeQtyPrecision;
      return convert(state.leverage, askPrice, { quantityPrecision }).margin(state.margin).quantity;
    }
    return get(quantityAtom);
  });
  const bidQuantityAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'margin') {
      const bidPrice = get(bidPriceAtom);
      const quantityPrecision = tradeQtyPrecision;
      return convert(state.leverage, bidPrice, { quantityPrecision }).margin(state.margin).quantity;
    }
    return get(quantityAtom);
  });
  // margin
  const marginAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    const price = get(priceAtom);
    const stateTo = convert(state.leverage, price)[state.kind](getKindValue(state));
    return stateTo.margin.toString();
  });
  const askMarginAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'quantity') {
      const askPrice = get(askPriceAtom);
      return convert(state.leverage, askPrice).quantity(state.quantity).margin;
    }
    return get(marginAtom);
  });
  const bidMarginAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'quantity') {
      const bidPrice = get(bidPriceAtom);
      return convert(state.leverage, bidPrice).quantity(state.quantity).margin;
    }
    return get(marginAtom);
  });
  // amount
  const amountAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    const price = get(priceAtom);
    const stateTo = convert(state.leverage, price)[state.kind](getKindValue(state));
    return stateTo.amount.toString();
  });
  const askAmountAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'quantity') {
      const askPrice = get(askPriceAtom);
      return convert(NaN /* leverage won't be used for this calculation */, askPrice)
        .quantity(state.quantity)
        .amount.toString();
    }
    return get(amountAtom);
  });
  const bidAmountAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'quantity') {
      const bidPrice = get(bidPriceAtom);
      return convert(NaN /* leverage won't be used for this calculation */, bidPrice)
        .quantity(state.quantity)
        .amount.toString();
    }
    return get(amountAtom);
  });
  return {
    marginAtom,
    quantityAtom,
    amountAtom,
    askMarginAtom,
    bidMarginAtom,
    askQuantityAtom,
    bidQuantityAtom,
    askAmountAtom,
    bidAmountAtom,
  };
}
